import { FileTextOutlined, PaperClipOutlined } from "@ant-design/icons"
import { Button, Upload, message } from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  RichTextEditor,
  type RichTextEditorHandle
} from "../../../../components/RichTextEditor/RichTextEditor.tsx"
import { RichTextViewer } from "../../../../components/RichTextEditor/RichTextViewer.tsx"
import { isRichTextEmpty } from "../../../../components/RichTextEditor/rich-text-html.ts"
import { validateFile } from "../../../../utils/file-validation.ts"
import { WorkspaceCard } from "../../../Lead/LeadWorkspacePage/cards/WorkspaceCard.tsx"
import { OverviewCardTitle } from "../../../Lead/LeadWorkspacePage/overview/OverviewCardIcon.tsx"
import type { Project, ProjectUpdateData } from "../../types/project.types.ts"
import {
  ALLOWED_FILE_EXTENSIONS,
  MAX_SIZE_MB
} from "./project-attachment-upload.ts"

const NOTE_ATTACHMENT_ACCEPT = ALLOWED_FILE_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(",")

interface ProjectAttachment {
  id: string
  file: string
  file_name?: string
  original_filename?: string
  is_archived?: boolean
}

interface NotesCardValues {
  internal_notes: string | null
}

interface ProjectNotesCardProps {
  project: Project
  canEdit?: boolean
  onSave: (changed: Partial<ProjectUpdateData>) => Promise<boolean>
}

/** The one remaining consumer of `RichTextEditor`/`RichTextViewer` — the
 * Lead workspace's equivalent card (`LeadViewInternalNotes`) was replaced by
 * a multi-note board (`LeadWorkspacePage/notes/`) that uses a plain
 * textarea instead, per the Lead Notes Board spec. */
function ProjectNotesCard({ project, canEdit, onSave }: ProjectNotesCardProps) {
  const { t } = useTranslation()
  const title = t("projectWorkspace.cards.notes.title")

  const attachmentName = (attachment?: ProjectAttachment | null): string =>
    attachment?.original_filename ||
    attachment?.file_name ||
    t("richTextEditor.attachment.fallbackName")

  const editorRef = useRef<RichTextEditorHandle>(null)
  const [uploadingName, setUploadingName] = useState<string | null>(null)
  const pendingUpload = useRef<{ name: string; priorIds: Set<string> } | null>(
    null
  )
  // Uploaded via this card during the edit session that's currently open —
  // cleared on save (they're referenced by the note now) and deleted on
  // Cancel/Discard (the note text that would have referenced them never
  // saved, so keeping the file would orphan it in the Files list forever).
  const sessionAttachmentIds = useRef<Set<string>>(new Set())

  const attachments = useMemo(
    () =>
      ((project?.attachments as unknown as ProjectAttachment[]) ?? []).filter(
        (file) => !file?.is_archived
      ),
    [project?.attachments]
  )
  const latestAttachments = useRef(attachments)
  latestAttachments.current = attachments

  useEffect(() => {
    const pending = pendingUpload.current
    if (!pending) return

    const match = attachments.find(
      (file) =>
        !pending.priorIds.has(file.id) && attachmentName(file) === pending.name
    )
    if (!match) return

    editorRef.current?.insertAttachmentChip({
      id: match.id,
      name: attachmentName(match)
    })
    sessionAttachmentIds.current.add(match.id)
    pendingUpload.current = null
    setUploadingName(null)
  }, [attachments])

  const attachmentsById = useMemo(
    () => new Map(attachments.map((file) => [file.id, file])),
    [attachments]
  )

  const values: NotesCardValues = {
    internal_notes: project?.internal_notes ?? null
  }

  const handleUpload = (file: File) => {
    const result = validateFile(file, {
      maxSize: MAX_SIZE_MB,
      allowedExtensions: ALLOWED_FILE_EXTENSIONS
    })
    if (result === Upload.LIST_IGNORE) return false

    pendingUpload.current = {
      name: file.name,
      priorIds: new Set(latestAttachments.current.map((f) => f.id))
    }
    setUploadingName(file.name)

    void onSave({
      file_list: [{ file_type: 5, file: { originFileObj: file } }]
    } as unknown as Partial<ProjectUpdateData>).then((ok) => {
      if (!ok) {
        pendingUpload.current = null
        setUploadingName(null)
        void message.error(t("richTextEditor.attachment.failed"))
      }
    })
    return false
  }

  const attachButton = canEdit && (
    <Upload
      showUploadList={false}
      accept={NOTE_ATTACHMENT_ACCEPT}
      disabled={!!uploadingName}
      beforeUpload={handleUpload}
    >
      <Button
        type={"text"}
        size={"small"}
        loading={!!uploadingName}
        icon={!uploadingName && <PaperClipOutlined />}
        aria-label={t("richTextEditor.toolbar.attach")}
      />
    </Upload>
  )

  return (
    <WorkspaceCard<NotesCardValues>
      cardId={"project-notes"}
      title={
        <OverviewCardTitle icon={<FileTextOutlined />} accent={"teal"}>
          {title}
        </OverviewCardTitle>
      }
      ariaTitle={title}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={async (changed) => {
        const ok = await onSave(changed)
        if (ok) sessionAttachmentIds.current.clear()
        return ok
      }}
      onCancel={() => {
        const ids = Array.from(sessionAttachmentIds.current)
        sessionAttachmentIds.current.clear()
        if (ids.length) {
          void onSave({
            deleted_attachment_ids: ids
          } as unknown as Partial<ProjectUpdateData>)
        }
      }}
      renderEdit={({ values, setValues, saving }) => (
        <RichTextEditor
          ref={editorRef}
          value={values.internal_notes ?? ""}
          onChange={(html) => setValues({ internal_notes: html })}
          placeholder={t("projectWorkspace.cards.notes.placeholder")}
          ariaLabel={title}
          autoFocus
          disabled={saving}
          minHeight={160}
          attachButton={attachButton}
        />
      )}
    >
      <div
        className={
          "flex-1 min-h-[140px] rounded-lg border border-dashed border-border p-3"
        }
      >
        {isRichTextEmpty(project?.internal_notes) ? (
          <span className={"text-gray-400"}>
            {t("projectWorkspace.cards.notes.empty")}
          </span>
        ) : (
          <RichTextViewer
            html={project?.internal_notes}
            resolveAttachment={(id) => {
              const file = attachmentsById.get(id)
              return file ? { url: file.file, name: attachmentName(file) } : null
            }}
            unavailableLabel={t("richTextEditor.attachment.unavailable")}
          />
        )}
      </div>
    </WorkspaceCard>
  )
}

export { ProjectNotesCard }
